import { DomSanitizer } from '@angular/platform-browser';

import { Component, Inject, Injectable } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { SafedomPipe } from 'src/app/shared/pipes/safedom.pipe';
import { ConfirmationDialogSettings, SanitizeType } from 'src/app/shared/models/confirmation-dialog-settings.model';

@Component({
    template: `
        <div [attr.data-qa-id]="dataQaId">
            <div class="nova__dialog__toolbar">
                <div class="nova__dialog__toolbar__title">
                    {{ title || action }}
                </div>
                <div class="nova__dialog__toolbar__controls">
                    <button
                        *ngIf="needCross"
                        class="nova__button--icon"
                        data-qa-id="CloseCrossButton"
                        (click)="close()">
                        <mat-icon class="nova-toolbar__controls__icon--close">close</mat-icon>
                    </button>
                </div>
            </div>
            <div
                mat-dialog-content
                class="nova__dialog__content">
                <div
                    data-qa-id="DialogContentMessage"
                    [innerHtml]="message"></div>
                <mat-dialog-actions
                    class="nova__dialog__content__actions"
                    [ngClass]="{ 'extra-padding': needExtraPadding }">
                    <button
                        (click)="confirm()"
                        mat-raised-button
                        color="primary"
                        data-qa-id="SuccessButton"
                        [mat-dialog-close]="true">
                        {{ action | uppercase }}
                    </button>
                    <button
                        mat-raised-button
                        color="default"
                        *ngIf="needCancel"
                        data-qa-id="CancelButton"
                        (click)="close()">
                        {{ cancelText | uppercase }}
                    </button>
                </mat-dialog-actions>
            </div>
        </div>
    `,
    styles: [
        `
            .extra-padding {
                margin-top: 48px !important;
                min-height: 10px;
                padding: 0px;
            }
            .mat-mdc-dialog-actions {
                padding: 0;
                min-height: auto;
            }
            .mat-mdc-dialog-actions[align='end'] {
                justify-content: flex-end;
            }
            .nova__dialog__content {
                letter-spacing: normal;
                line-height: normal;
            }
        `
    ]
})
export class ConfirmDialogComponent {
    message;

    action: string;

    onClose: Subject<boolean> = new Subject();

    title: string;

    needCancel = true;

    needCross = false;

    cancelText: string;

    type: SanitizeType;

    needExtraPadding = true;

    dataQaId: string;

    constructor(
        private readonly dialogRef: MatDialogRef<ConfirmDialogComponent>,
        private readonly domSanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA)
        private readonly data: {
            message;
            action: string;
            title: string;
            needCancel?: boolean;
            cancelText?: string;
            needCross?: boolean;
            type?: SanitizeType;
            needExtraPadding?: boolean;
            dataQaId?: string;
        }
    ) {
        if (
            data.type === SanitizeType.html ||
            data.type === SanitizeType.style ||
            data.type === SanitizeType.script ||
            data.type === SanitizeType.url ||
            data.type === SanitizeType.resourceUrl
        ) {
            const pipe = new SafedomPipe(this.domSanitizer);
            this.message = pipe.transform(data.message, data.type);
        } else {
            this.message = data.message;
        }

        this.action = data.action;
        this.title = data.title;
        this.cancelText = data.cancelText;

        if (!this.cancelText) {
            this.cancelText = 'Cancel';
        }

        if (data.needCancel !== undefined) {
            this.needCancel = data.needCancel;
        }

        if (data.needCross !== undefined) {
            this.needCross = data.needCross;
        }

        if (data.needExtraPadding !== undefined) {
            this.needExtraPadding = data.needExtraPadding;
        }

        if (data.dataQaId !== undefined) {
            this.dataQaId = data.dataQaId;
        }
    }

    close(): void {
        this.onClose.next(true);
        this.dialogRef.close();
    }

    confirm(): void {
        this.onClose.next(false);
    }
}

@Injectable({
    providedIn: 'root'
})
export class ConfirmDialogService {
    constructor(private readonly dialog: MatDialog) {}

    /**
     * @deprecated - Please use the new shared effect
     *
     * @param {string} message
     * @param {string} action
     * @param {string} title
     * @param {boolean} [needCancel]
     * @param {string} [cancelText]
     * @param {SanitizeType} [type]
     * @param {string} [panelClass]
     * @returns {Observable<boolean>}
     * @memberof ConfirmDialogService
     */
    show(
        message: string,
        action: string,
        title: string,
        needCancel?: boolean,
        cancelText?: string,
        type?: SanitizeType,
        panelClass?: string
    ): Observable<boolean> {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '528px',
            minWidth: '400px',
            data: {
                message: message,
                action: action,
                title: title,
                needCancel: needCancel,
                cancelText: cancelText,
                type: type
            },
            panelClass: panelClass ? panelClass : ['nova__dialog', 'nova__dialog--show-above-all']
        });

        return dialogRef.afterClosed();
    }

    showDialog(settings: ConfirmationDialogSettings): Observable<boolean> {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '528px',
            minWidth: '400px',
            disableClose: settings.disableClose ?? false,
            data: {
                message: settings.message,
                action: settings.action,
                title: settings.title,
                needCancel: settings.needCancel,
                cancelText: settings.cancelText,
                type: settings.type,
                addtlData: settings?.addtlData ?? undefined,
                needExtraPadding: settings.needExtraPadding,
                needCross: settings.needCross ?? false,
                dataQaId: settings.dataQaId ?? ''
            },
            panelClass: settings.panelClass ? settings.panelClass : ['nova__dialog', 'nova__dialog--show-above-all'],
            autoFocus: typeof settings.autoFocus === 'boolean' ? settings.autoFocus : true
        });

        return dialogRef.afterClosed();
    }
}
